import B from "../../assets/dashboard(flight&ride)/building.svg";
import C from "../../assets/dashboard(flight&ride)/calender.svg";
import P from "../../assets/dashboard(flight&ride)/person.svg";
import W from "../../assets/dashboard(flight&ride)/wallet.svg";
import U from "../../assets/dashboard(flight&ride)/alty.svg";
import D from "../../assets/dashboard(flight&ride)/blty.svg";
import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Stack,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import BackButton from "components/BackButton";
import DashboardLayout from "layout";
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import ReUseAbleTable, { Column } from "shared/ReUseAbleTable";
import { getStatusStyle } from "utils/misc";
import { toLocalDate } from "utils/helpers";
import { getBookingStats, sendBookingStats } from "services/booking";
import StatsCardGrid from "components/StatsCardGrid";
import { LoadingState } from "components/LoadingState";

interface BookingStatsPayload {
  email: string;
  from: string;
  to: string;
}

interface IFlightStat {
  total_booking: number;
  total_user: number;
  total_sales: number;
}

interface IRecentOrder {
  id: string;
  booking_id: string;
  // pnr: string;
  airline: string;
  from: string;
  to: string;
  contact: string;
  amount: number;
  status: React.ReactNode;
  date: string;
}

const AirlineDetails: React.FC = () => {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const columns: Column[] = [
    { id: "booking_id", label: "Booking ID" },
    { id: "airline", label: "Airline Name" },
    { id: "amount", label: "Amount" },
    { id: "from", label: "From" },
    { id: "to", label: "To" },
    // { id: "pnr", label: "PNR" },
    { id: "contact", label: "Contact" },
    { id: "status", label: "Status" },
    { id: "date", label: "Date" },
  ];

  // React Query (not a mount effect) so revisits reuse the cached stats.
  const { data: bookingStats, isLoading: isFetchLoading } = useQuery({
    queryKey: ["bookingStats", 1, 10],
    queryFn: () => getBookingStats(1, 10),
  });
  const flightStats: IFlightStat = bookingStats?.data?.FlightSummary ?? {
    total_booking: 0,
    total_user: 0,
    total_sales: 0,
  };
  const recentOrders: IRecentOrder[] = (bookingStats?.data?.RecentOrders || []).map(
          (order: IRecentOrder) => ({
            id: order.id,
            booking_id: order.booking_id,
            airline: order.airline,
            amount: order.amount.toLocaleString("en-us") + "NGN ",
            from: order.from,
            to: order.to,
            // pnr: order.pnr,
            contact: order.contact,
            status: (
              <Chip
                label={order.status}
                sx={getStatusStyle(`${order.status || "pending"}`)}
              />
            ),
            date: order.date.split("T")[0],
          })
        );

  const flightShowUps = [
    {
      title: "Total Bookings",
      value: flightStats.total_booking.toLocaleString("en-us"),
      icon: C,
      // rating: "8.5",
      arrow: U,
      bg: "#FFEDCE",
      res: "Up from yesterday",
    },
    {
      title: "Total User",
      value: flightStats.total_user.toLocaleString("en-us"),
      icon: P,
      // rating: "8.5",
      arrow: U,
      bg: "#E8FFF2",
      res: "Up from yesterday",
    },
    {
      title: "Total Sales",
      value: flightStats.total_sales.toLocaleString("en-us"),
      icon: W,
      // rating: "8.5",
      arrow: D,
      aColor: "#DA4733",
      bg: "#E8FFF2",
      res: "Down from yesterday",
    },
    {
      title: "Total Airlines",
      value: "10",
      icon: B,
      // rating: "8.5",
      arrow: D,
      aColor: "#DA4733",
      bg: "#E8FFF2",
      res: "Down from yesterday",
    },
  ];

  const navigate = useNavigate();

  const handleOpenDialog = (): void => {
    setDialogOpen(true);
  };

  const handleCloseDialog = (): void => {
    setDialogOpen(false);
    setEmail("");
    setFromDate(null);
    setToDate(null);
    setError("");
    setShowSuccess(false);
  };

  const handleSendBookingHistory = async (): Promise<void> => {
    if (!email || !fromDate || !toDate) {
      setError("Please fill all required fields");
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      const formattedFromDate: string = fromDate ? toLocalDate(fromDate) : "";
      const formattedToDate: string = toDate ? toLocalDate(toDate) : "";

      const payload: BookingStatsPayload = {
        email,
        from: formattedFromDate,
        to: formattedToDate,
      };

      // Call the API endpoint using the service
      const response = await sendBookingStats(payload);
      if (response.success) {
        setShowSuccess(true);
      }
    } catch (err) {
      console.error("Error sending booking stats:", err);
      setError("Failed to send booking stats. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setEmail(event.target.value);
  };

  const handleFromDateChange = (newValue: Date | null): void => {
    setFromDate(newValue);
  };

  const handleToDateChange = (newValue: Date | null): void => {
    setToDate(newValue);
  };

  const isEmailValid: boolean = email.includes("@") && email.includes(".");

  return (
    <React.Fragment>
      {(isFetchLoading || error) && <LoadingState />}

      {!isFetchLoading && (
        <React.Fragment>
          <DashboardLayout nextSideBar>
            {/* Forced Back button to fit layout */}
            <Box
              sx={{ background: "white", mt: "-42px", mx: "-22px", mb: "30px" }}
            >
              <BackButton showStroke={false} />
            </Box>

            <StatsCardGrid items={flightShowUps} />

            <Box
              mt="30px"
              mb="24px"
              display="flex"
              gap="10px"
              justifyContent="space-between"
            >
              <div>
                <Typography color="#101828" fontSize="24px" fontWeight={600}>
                  Manage Bookings
                </Typography>
              </div>

              <Button
                onClick={handleOpenDialog}
                variant="contained"
                sx={{ height: "40px", fontSize: "14px" }}
              >
                Send Booking History
              </Button>
            </Box>
            <ReUseAbleTable
              columns={columns}
              rows={recentOrders}
              onClick={(row) =>
                navigate(`/bookings/flightorderdetails/${row.id}`)
              }
            />
          </DashboardLayout>
          <Dialog
            open={dialogOpen}
            onClose={handleCloseDialog}
            maxWidth="xs"
            fullWidth
          >
            {!showSuccess ? (
              <>
                <DialogTitle
                  sx={{
                    m: 0,
                    py: 2,
                    px: "25px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid #F9F9F9",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "16px", fontWeight: "600" }}
                    variant="h6"
                    component="div"
                  >
                    Enter Email
                  </Typography>
                  <IconButton
                    aria-label="close"
                    onClick={handleCloseDialog}
                    sx={{}}
                  >
                    <Close />
                  </IconButton>
                </DialogTitle>
                <DialogContent sx={{ px: "25px", mt: "20px" }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Stack spacing={2}>
                      <TextField
                        placeholder="mail@gmail.com"
                        variant="outlined"
                        sx={{
                          bgcolor: "#F9F9F9",
                          borderRadius: "10px",
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              border: "none", // Remove the border
                            },
                          },
                        }}
                        fullWidth
                        type="email"
                        value={email}
                        onChange={handleEmailChange}
                      />

                      <DatePicker
                        label="From Date"
                        value={fromDate}
                        onChange={handleFromDateChange}
                        slots={{
                          textField: TextField,
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: "outlined" as const,
                            sx: {
                              bgcolor: "#F9F9F9",
                              borderRadius: "10px",
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  border: "none",
                                },
                                "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                                  color: "#444",
                                },
                              },
                            },
                          },
                        }}
                      />

                      <DatePicker
                        label="To Date"
                        value={toDate}
                        onChange={handleToDateChange}
                        minDate={fromDate || new Date()}
                        slots={{
                          textField: TextField,
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: "outlined" as const,
                            sx: {
                              bgcolor: "#F9F9F9",
                              borderRadius: "10px",
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  border: "none",
                                },
                                "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                                  color: "#444",
                                },
                              },
                            },
                          },
                        }}
                      />

                      {error && (
                        <Typography color="error" variant="body2">
                          {error}
                        </Typography>
                      )}
                    </Stack>
                  </LocalizationProvider>
                </DialogContent>
                <DialogActions sx={{ px: "20px", pb: "20px" }}>
                  <Button
                    onClick={handleSendBookingHistory}
                    variant="contained"
                    fullWidth
                    disabled={isLoading || !email || !fromDate || !toDate}
                    sx={{
                      height: "40px",
                      fontSize: "14px",
                      borderRadius: "20px",
                    }}
                  >
                    {isLoading ? "Sending..." : "Confirm"}
                  </Button>
                </DialogActions>
              </>
            ) : (
              <DialogContent sx={{ py: "25px" }}>
                <Box
                  sx={{ display: "flex", justifyContent: "center", w: "100%" }}
                >
                  <TickIcon />
                </Box>

                <Typography
                  sx={{
                    fontSize: "18px",
                    fontWeight: "600",
                    textAlign: "center",
                    mt: "20px",
                  }}
                >
                  Booking History Sent
                </Typography>

                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#667085",
                    textAlign: "center",
                  }}
                >
                  The booking history has been successfully sent
                </Typography>

                <Button
                  onClick={handleCloseDialog}
                  variant="contained"
                  fullWidth
                  sx={{
                    height: "40px",
                    fontSize: "14px",
                    borderRadius: "20px",
                    mt: "40px",
                  }}
                >
                  Close
                </Button>
              </DialogContent>
            )}
          </Dialog>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default AirlineDetails;

const TickIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="50"
    height="50"
    viewBox="0 0 62 62"
    fill="none"
  >
    <path
      d="M17.5 31L26.5 40L44.5 22M61 31C61 47.5685 47.5685 61 31 61C14.4315 61 1 47.5685 1 31C1 14.4315 14.4315 1 31 1C47.5685 1 61 14.4315 61 31Z"
      stroke="#379E66"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
